import type { AppDispatch } from '../../../hooks/useAppDispatch';
import systemMasterTypes from './SystemMasters.type';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import { ERROR_MESSAGE } from '../../../Components/helper';
import {
  deleteSubBomAttribute,
  fetchSubBomAttributes,
  saveSubBomAttributes,
  setSubBomAttributeActive,
  updateSubBomAttributeSequence,
  SubBomAttribute,
  SubBomAttributeEnvelope,
} from '../../../services/sub-bom-custom-attribute.service';

const { CUSTOM_ATTRIBUTE_SUB_BOM_LIST } = systemMasterTypes;

export { CUSTOM_ATTRIBUTE_SUB_BOM_LIST };

const SUCCESS_CODE = 200;

const FALLBACK_ERROR = 'SUB_BOM_CUSTOM_ATTRIBUTE.SOMETHING_WENT_WRONG';

export interface SubBomAttributeView extends SubBomAttribute {
  _id: string;
  bomTypeId: string;
  seqNumber: number;
  isActive: boolean;
}

export interface SubBomSequenceEntry {
  _id: string;
  sequence: number;
}

type TranslateFn = (key: string) => string;

/**
 * Reports the outcome of a write. The endpoints answer with a translation key
 * rather than prose, so the server's own validation messages surface in the
 * user's language without the screen restating them.
 * @param {SubBomAttributeEnvelope<unknown>} envelope - The response envelope.
 * @param {string} successKey - Key to show when the write succeeded.
 * @param {TranslateFn} translate - The screen's translate function.
 * @returns {boolean} True when the write succeeded.
 */
function reportOutcome(
  envelope: SubBomAttributeEnvelope<unknown>,
  successKey: string,
  translate: TranslateFn,
): boolean {
  if (envelope?.code === SUCCESS_CODE) {
    toasterMsg(translate(successKey), 'success');
    return true;
  }
  toasterMsg(translate(envelope?.message || FALLBACK_ERROR), 'danger');
  return false;
}

/**
 * Loads the sub-BOM custom attributes for one BOM Type into the store.
 * @param {string} bomTypeId - The BOM Type to load.
 * @returns {Function} The thunk.
 */
export const getSubBomCustomAttributes =
  (bomTypeId: string) =>
  async (dispatch: AppDispatch): Promise<SubBomAttributeEnvelope<SubBomAttributeView[]> | undefined> => {
    try {
      const res = await fetchSubBomAttributes<SubBomAttributeView[]>(bomTypeId);
      dispatch({
        type: CUSTOM_ATTRIBUTE_SUB_BOM_LIST,
        payload: res?.code === SUCCESS_CODE ? res.data : [],
      });
      return res;
    } catch (error) {
      toasterMsg(ERROR_MESSAGE, 'danger');
      return undefined;
    }
  };

/**
 * Saves the whole attribute list for one BOM Type and refreshes the store from
 * the response, so the screen immediately shows the ids and order the server
 * assigned.
 * @param {string} bomTypeId - The BOM Type being saved.
 * @param {SubBomAttribute[]} attributes - The complete attribute list.
 * @param {TranslateFn} translate - The screen's translate function.
 * @returns {Function} The thunk.
 */
export const saveSubBomCustomAttributes =
  (bomTypeId: string, attributes: SubBomAttribute[], translate: TranslateFn) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    try {
      const res = await saveSubBomAttributes<SubBomAttributeView[]>(bomTypeId, attributes);
      if (res?.code === SUCCESS_CODE) {
        dispatch({ type: CUSTOM_ATTRIBUTE_SUB_BOM_LIST, payload: res.data });
      }
      return reportOutcome(res, 'SUB_BOM_CUSTOM_ATTRIBUTE.SAVED', translate);
    } catch (error) {
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/**
 * Switches an attribute, or one of its options, active or inactive.
 * @param {string} attributeId - The attribute to change.
 * @param {boolean} isActive - The state to apply.
 * @param {string | null} optionId - The option to change, when narrowing to one.
 * @param {TranslateFn} translate - The screen's translate function.
 * @returns {Function} The thunk.
 */
export const setSubBomAttributeActiveState =
  (attributeId: string, isActive: boolean, optionId: string | null, translate: TranslateFn) =>
  async (): Promise<boolean> => {
    try {
      const res = await setSubBomAttributeActive(attributeId, isActive, optionId);
      return reportOutcome(res, 'SUB_BOM_CUSTOM_ATTRIBUTE.STATE_UPDATED', translate);
    } catch (error) {
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/**
 * Soft-deletes an attribute, or just the named options of one.
 * @param {string} attributeId - The attribute to delete, or the option's owner.
 * @param {string[]} optionIds - The options to delete, when narrowing to them.
 * @param {TranslateFn} translate - The screen's translate function.
 * @returns {Function} The thunk.
 */
export const deleteSubBomCustomAttribute =
  (attributeId: string, optionIds: string[], translate: TranslateFn) =>
  async (): Promise<boolean> => {
    try {
      const res = await deleteSubBomAttribute(attributeId, optionIds);
      return reportOutcome(res, 'SUB_BOM_CUSTOM_ATTRIBUTE.DELETED', translate);
    } catch (error) {
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/**
 * Persists a new display order.
 * @param {SubBomSequenceEntry[]} sequence - Ids paired with their new position.
 * @param {TranslateFn} translate - The screen's translate function.
 * @returns {Function} The thunk.
 */
export const setSubBomAttributeSequence =
  (sequence: SubBomSequenceEntry[], translate: TranslateFn) =>
  async (): Promise<boolean> => {
    try {
      const res = await updateSubBomAttributeSequence(sequence);
      return reportOutcome(res, 'SUB_BOM_CUSTOM_ATTRIBUTE.SEQUENCE_UPDATED', translate);
    } catch (error) {
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };
